import { SupportClaimEligibility } from '../../../../api';
import { renderAddressGroup, renderBoolean, renderDate } from '../../../data-table';
import {
    EligibilityDependentDetails,
    EligibilityItemsDetails,
    EligibilityValidationsDetails,
} from '../../eligibility';
import _ from 'lodash';
import React from 'react';
import { ExpandCol, ExpandTable, ExpandTBody, TablesGroup } from '../claim-timeline.styles';
import { DateRow, TextRow } from '../timeline-row-components';
import * as S from './eligibility-timeline-detail.styles';
import {
    EligibilityTimelineEligibility,
    IEligibilityTimelineDescriptionProps,
    IEligibilityTimelineDetailProps,
} from './eligibility-timeline-detail.types';

function isSupportEligibility(
    eligibility: EligibilityTimelineEligibility,
): eligibility is SupportClaimEligibility {
    return 'clearinghousePayer' in eligibility || 'externalId' in eligibility;
}

export const EligibilityTimelineDescription: React.FunctionComponent<IEligibilityTimelineDescriptionProps> = React.memo(
    ({ eligibility }) => {
        const name = _.compact([eligibility.first, eligibility.last]).join(' ');
        const planDate = _.compact([eligibility.planStartDate, eligibility.planEndDate]).join(' - ');
        const planID = eligibility.planId ? `Plan ID: ${eligibility.planId}` : undefined;
        const description = _.compact([name, planID, planDate]).join(', ');

        return (
            <S.DescriptionContainer>
                <S.DescriptionBody>
                    <S.TR>
                        <S.TD>
                            <S.LabelWithIcon>
                                <S.StatusIconWrapper>
                                    {renderBoolean(_.size(eligibility.validations) === 0)}
                                </S.StatusIconWrapper>
                                {description}
                            </S.LabelWithIcon>
                        </S.TD>
                    </S.TR>
                </S.DescriptionBody>
            </S.DescriptionContainer>
        );
    },
);

export const EligibilityTimelineDetail: React.FunctionComponent<IEligibilityTimelineDetailProps> = React.memo(
    ({ eligibility }) => {
        const name = _.compact([eligibility.first, eligibility.last]).join(' ');
        const supportEligibility = isSupportEligibility(eligibility) ? eligibility : undefined;

        return (
            <ExpandCol>
                <TablesGroup>
                    <div>
                        <ExpandTable>
                            <ExpandTBody>
                                <TextRow label="Eligibility ID" value={eligibility.id} />
                                <TextRow label="Eligibility Type" value={supportEligibility?.type} />
                                <TextRow label="Member Name" value={name} />
                                <DateRow label="Member DOB" shortDate={true} value={eligibility.dob} />
                                <TextRow label="Member Gender" value={eligibility.sex} />
                                <TextRow
                                    label="Member Address"
                                    value={
                                        eligibility.address ? renderAddressGroup([eligibility.address]) : undefined
                                    }
                                />
                                <TextRow label="Member Other Qualifier ID" value={eligibility.otherQualifierId} />
                                <TextRow label="Member Other ID" value={eligibility.otherId} />
                                <TextRow label="Member Other Description" value={eligibility.otherDescription} />
                            </ExpandTBody>
                        </ExpandTable>
                    </div>
                    <div>
                        <ExpandTable>
                            <ExpandTBody>
                                <TextRow label="External ID" value={supportEligibility?.externalId} />
                                <TextRow label="Group Name" value={eligibility.groupDescription} />
                                <TextRow label="Group ID" value={eligibility.groupId} />
                                <TextRow label="Plan Name" value={eligibility.planDescription} />
                                <TextRow label="Plan ID" value={eligibility.planId} />
                                {(eligibility.planStartDate || eligibility.planEndDate) && (
                                    <TextRow
                                        label="Plan"
                                        value={_.join(
                                            _.map([eligibility.planStartDate, eligibility.planEndDate], renderDate),
                                            ' — ',
                                        )}
                                    />
                                )}
                                <TextRow
                                    label="Clearinghouse"
                                    value={supportEligibility?.clearinghousePayer?.clearinghouse?.name}
                                />
                                <TextRow
                                    label="Clearinghouse Payer"
                                    value={supportEligibility?.clearinghousePayer?.payer?.name}
                                />
                                <TextRow
                                    label="Clearinghouse External ID"
                                    value={supportEligibility?.clearinghousePayer?.externalId}
                                />
                            </ExpandTBody>
                        </ExpandTable>
                    </div>
                </TablesGroup>
                {!!eligibility.validations?.length && (
                    <EligibilityValidationsDetails validations={eligibility.validations} />
                )}
                {!!eligibility.items?.length && <EligibilityItemsDetails items={eligibility.items} />}
                {!!eligibility.dependents?.length && (
                    <EligibilityDependentDetails dependents={eligibility.dependents} />
                )}
            </ExpandCol>
        );
    },
);
